import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { AccountSuspensionPanel } from "../components/admin/AccountSuspensionPanel";
import { AwaitingPayouts } from "../components/admin/AwaitingPayouts";
import { PayoutStatusPill } from "../components/admin/PayoutStatusPill";
import { ModalShell } from "../components/ModalShell";
import { InlineIcon } from "../components/InlineIcon";
import { OrganisationDetailsModal } from "../components/admin/OrganisationDetailsModal";
import { InstitutionDetailsModal } from "../components/admin/InstitutionDetailsModal";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAdminStats,
  fetchPendingOrgs,
  fetchAdminProfessionals,
  fetchAdminInstitutions,
  fetchAdminOrganisations,
  fetchAdminVerifications,
  fetchAdminRevenue,
  fetchAdminPayouts,
  approveInstitution,
  approveOrganisation,
  rejectInstitution,
  rejectOrganisation,
} from "../api/admin";
import { createDownloadUrl } from "../api/storage";
import { C, font } from "../styles/tokens";
import { formatRupees } from "../utils/money";
import { isRefund, ledgerAmountText } from "../utils/ledger";
import { suspensionActionFor } from "../utils/accountSuspension";

/**
 * AdminDashboard - the platform admin's central control hub.
 *
 * Provides:
 *   - Overview statistic cards for Professionals, Institutions, Organisations, and Pending Review
 *   - Interactive drill-down modals for all 4 categories with real-time search, filters, and actions
 *   - Verifications & Finance summaries with clickable drill-down modals
 *   - Quick access to administrative tools & audit trail
 */
export function AdminDashboard() {
  const { localUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = localUser?.type === "PLATFORM_ADMIN";

  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active modal state: null | 'PROFESSIONALS' | 'INSTITUTIONS' | 'ORGANISATIONS' | 'PENDING_REVIEW' | 'VERIFICATIONS' | 'PLATFORM_REVENUE' | 'PENDING_PAYOUTS'
  const [activeModal, setActiveModal] = useState(null);

  // Modal data states
  const [professionalsList, setProfessionalsList] = useState([]);
  const [institutionsList, setInstitutionsList] = useState([]);
  const [organisationsList, setOrganisationsList] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [verificationsList, setVerificationsList] = useState([]);
  const [revenueList, setRevenueList] = useState([]);
  const [payoutsList, setPayoutsList] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Search & filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Inline action state for pending review modal
  const [actionTarget, setActionTarget] = useState(null); // { item, kind, mode: 'approve' | 'reject' }
  const [actionReason, setActionReason] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionSuccessMsg, setActionSuccessMsg] = useState(null);
  // The professional whose account is being suspended or reinstated
  const [accountTarget, setAccountTarget] = useState(null);
  // Detailed view modals
  const [selectedOrgDetail, setSelectedOrgDetail] = useState(null);
  const [selectedInstDetail, setSelectedInstDetail] = useState(null);

  const loadDashboardData = async () => {
    try {
      const [s, p] = await Promise.all([fetchAdminStats(), fetchPendingOrgs()]);
      setStats(s);
      setPending(p);
      setPendingList(p?.items || []);
    } catch (err) {
      setError(err.message ?? "Failed to load admin dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    loadDashboardData();
  }, [isAdmin]);

  // After a payout is recorded: the ledger it settled, and the totals above.
  const reloadPayouts = async () => {
    const [data] = await Promise.all([
      fetchAdminPayouts(),
      loadDashboardData(),
    ]);
    setPayoutsList(data || []);
  };

  // Open modal and fetch respective data
  const handleOpenModal = async (modalType, initialFilter = "ALL") => {
    setActiveModal(modalType);
    setSearchQuery("");
    setStatusFilter(initialFilter);
    setCurrentPage(1);
    setModalError(null);
    setActionTarget(null);
    setAccountTarget(null);
    setActionSuccessMsg(null);
    setModalLoading(true);

    try {
      if (modalType === "PROFESSIONALS") {
        const data = await fetchAdminProfessionals();
        setProfessionalsList(data || []);
      } else if (modalType === "INSTITUTIONS") {
        const data = await fetchAdminInstitutions();
        setInstitutionsList(data || []);
      } else if (modalType === "ORGANISATIONS") {
        const data = await fetchAdminOrganisations();
        setOrganisationsList(data || []);
      } else if (modalType === "PENDING_REVIEW") {
        const data = await fetchPendingOrgs();
        setPendingList(data?.items || []);
        setPending(data);
      } else if (modalType === "VERIFICATIONS") {
        const data = await fetchAdminVerifications();
        setVerificationsList(data || []);
      } else if (modalType === "PLATFORM_REVENUE") {
        const data = await fetchAdminRevenue();
        setRevenueList(data || []);
      } else if (modalType === "PENDING_PAYOUTS") {
        const data = await fetchAdminPayouts();
        setPayoutsList(data || []);
      }
    } catch (err) {
      setModalError(
        err.message ?? `Failed to load ${modalType.toLowerCase()} list.`,
      );
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setActionTarget(null);
    setAccountTarget(null);
    setSelectedOrgDetail(null);
    setSelectedInstDetail(null);
    setActionReason("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  // Inline approval / rejection handler
  const handleDecidePending = async () => {
    if (!actionTarget) return;
    setActionSubmitting(true);
    setModalError(null);

    try {
      const { item, kind, mode } = actionTarget;
      const note = actionReason.trim() || undefined;

      if (mode === "approve") {
        if (kind === "INSTITUTION") {
          await approveInstitution(item.id, note);
        } else {
          await approveOrganisation(item.id, note);
        }
        setActionSuccessMsg(`${item.name} has been successfully approved!`);
      } else {
        if (!actionReason.trim()) {
          throw new Error("A rejection reason is required.");
        }
        if (kind === "INSTITUTION") {
          await rejectInstitution(item.id, actionReason.trim());
        } else {
          await rejectOrganisation(item.id, actionReason.trim());
        }
        setActionSuccessMsg(`${item.name} has been rejected.`);
      }

      // Refresh pending list and dashboard stats
      const updatedPending = await fetchPendingOrgs();
      setPendingList(updatedPending?.items || []);
      setPending(updatedPending);
      const updatedStats = await fetchAdminStats();
      setStats(updatedStats);

      setActionTarget(null);
      setActionReason("");
      setTimeout(() => setActionSuccessMsg(null), 4000);
    } catch (err) {
      setModalError(err.message ?? "Action failed. Please try again.");
    } finally {
      setActionSubmitting(false);
    }
  };

  // After a suspension or its lifting: the list and the counts above.
  const handleAccountChanged = async (message) => {
    setAccountTarget(null);
    setActionSuccessMsg(message);
    setModalError(null);
    try {
      const [list, updatedStats] = await Promise.all([
        fetchAdminProfessionals(),
        fetchAdminStats(),
      ]);
      setProfessionalsList(list || []);
      setStats(updatedStats);
    } catch (err) {
      setModalError(err.message ?? "The list couldn't be refreshed.");
    }
  };

  // Filtered lists for modals
  const filteredProfessionals = useMemo(() => {
    return professionalsList.filter((p) => {
      const matchesSearch =
        !searchQuery ||
        p.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.phone?.includes(searchQuery) ||
        p.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.state?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "VERIFIED" && p.isVerified) ||
        (statusFilter === "UNVERIFIED" && !p.isVerified) ||
        (statusFilter === "ACTIVE" && p.status === "ACTIVE") ||
        (statusFilter === "SUSPENDED" && p.status === "SUSPENDED");

      return matchesSearch && matchesStatus;
    });
  }, [professionalsList, searchQuery, statusFilter]);

  const paginatedProfessionals = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProfessionals.slice(start, start + pageSize);
  }, [filteredProfessionals, currentPage, pageSize]);

  const filteredInstitutions = useMemo(() => {
    return institutionsList.filter((inst) => {
      const matchesSearch =
        !searchQuery ||
        inst.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.registrationNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        inst.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.primaryOfficer?.fullName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || inst.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [institutionsList, searchQuery, statusFilter]);

  const paginatedInstitutions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredInstitutions.slice(start, start + pageSize);
  }, [filteredInstitutions, currentPage, pageSize]);

  const filteredOrganisations = useMemo(() => {
    return organisationsList.filter((org) => {
      const matchesSearch =
        !searchQuery ||
        org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.registrationNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        org.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.primaryOfficer?.fullName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || org.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [organisationsList, searchQuery, statusFilter]);

  const paginatedOrganisations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredOrganisations.slice(start, start + pageSize);
  }, [filteredOrganisations, currentPage, pageSize]);

  const filteredPending = useMemo(() => {
    return pendingList.filter((entry) => {
      const item = entry.item;
      const matchesSearch =
        !searchQuery ||
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.registrationNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        item.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.kind?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || entry.kind === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [pendingList, searchQuery, statusFilter]);

  const paginatedPending = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPending.slice(start, start + pageSize);
  }, [filteredPending, currentPage, pageSize]);

  const filteredVerifications = useMemo(() => {
    return verificationsList.filter((v) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        v.credential?.title?.toLowerCase().includes(q) ||
        v.credential?.type?.toLowerCase().includes(q) ||
        v.professional?.fullName?.toLowerCase().includes(q) ||
        v.professional?.email?.toLowerCase().includes(q) ||
        v.verifierName?.toLowerCase().includes(q) ||
        v.decidedByName?.toLowerCase().includes(q) ||
        v.decisionReason?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === "ALL" || v.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [verificationsList, searchQuery, statusFilter]);

  const paginatedVerifications = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVerifications.slice(start, start + pageSize);
  }, [filteredVerifications, currentPage, pageSize]);

  const filteredRevenue = useMemo(() => {
    return revenueList.filter((r) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        r.payerName?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.providerPaymentId?.toLowerCase().includes(q) ||
        r.providerOrderId?.toLowerCase().includes(q) ||
        r.invoiceNumber?.toLowerCase().includes(q) ||
        r.type?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === "ALL" || r.type === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [revenueList, searchQuery, statusFilter]);

  const paginatedRevenue = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRevenue.slice(start, start + pageSize);
  }, [filteredRevenue, currentPage, pageSize]);

  const filteredPayouts = useMemo(() => {
    return payoutsList.filter((p) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        p.verifierName?.toLowerCase().includes(q) ||
        p.contactEmail?.toLowerCase().includes(q) ||
        p.regNumber?.toLowerCase().includes(q) ||
        p.credentialTitle?.toLowerCase().includes(q) ||
        p.settlementBatchId?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q);

      let matchesStatus = true;
      if (statusFilter === "PENDING" || statusFilter === "UNSETTLED") {
        matchesStatus = !p.isSettled;
      } else if (statusFilter === "SETTLED") {
        matchesStatus = p.isSettled;
      } else if (statusFilter === "INSTITUTION") {
        matchesStatus = p.verifierType === "INSTITUTION";
      } else if (statusFilter === "ORGANISATION") {
        matchesStatus = p.verifierType === "ORGANISATION";
      }

      return matchesSearch && matchesStatus;
    });
  }, [payoutsList, searchQuery, statusFilter]);

  const paginatedPayouts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPayouts.slice(start, start + pageSize);
  }, [filteredPayouts, currentPage, pageSize]);

  if (!isAdmin) {
    return (
      <AppShell>
        <div style={cardStyle}>This page is for platform administrators.</div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div style={{ color: C.textMed, padding: 24, textAlign: "center" }}>
          Loading admin dashboard...
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-4">
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            margin: 0,
            color: C.textDark,
          }}
        >
          Platform admin
        </h1>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
          Admin Control Center
        </span>
      </div>

      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Pending review banner - most actionable item */}
      {pending && pending.total > 0 && (
        <section
          style={{
            ...cardStyle,
            background: C.amberLight,
            borderColor: C.amber + "33",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: C.amber,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              Action required
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: C.textDark,
                marginTop: 4,
              }}
            >
              {pending.total}{" "}
              {pending.total === 1 ? "organisation" : "organisations"} awaiting
              review
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleOpenModal("PENDING_REVIEW")}
            style={{
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.amber,
              border: 0,
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: font,
              whiteSpace: "nowrap",
              boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            Review queue ({pending.total})
          </button>
        </section>
      )}

      {/* Interactive Stats grid */}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="Professionals"
          value={stats?.users.professionals ?? 0}
          icon="user"
          onClick={() => handleOpenModal("PROFESSIONALS")}
        />
        <StatCard
          label="Institutions"
          value={stats?.orgs.institutions ?? 0}
          icon="academic"
          onClick={() => handleOpenModal("INSTITUTIONS")}
        />
        <StatCard
          label="Organisations"
          value={stats?.orgs.organisations ?? 0}
          icon="building"
          onClick={() => handleOpenModal("ORGANISATIONS")}
        />
        <StatCard
          label="Pending review"
          value={stats?.orgs.pendingReview ?? 0}
          highlight={(stats?.orgs.pendingReview ?? 0) > 0}
          icon="clock"
          onClick={() => handleOpenModal("PENDING_REVIEW")}
        />
      </section>

      {/* Verifications & Finance Section */}
      <section
        data-responsive-grid="2"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {/* Verifications card */}
        <div style={cardStyle}>
          <div className="flex items-center justify-between">
            <h2 style={cardTitleStyle}>Verifications</h2>
            <button
              type="button"
              onClick={() => handleOpenModal("VERIFICATIONS", "ALL")}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
            >
              View all requests
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 14,
            }}
          >
            <SmallStat
              label="Pending"
              value={stats?.verifications.pending ?? 0}
              color={C.amber}
              onClick={() => handleOpenModal("VERIFICATIONS", "PENDING")}
            />
            <SmallStat
              label="Approved"
              value={stats?.verifications.approved ?? 0}
              color={C.green}
              onClick={() => handleOpenModal("VERIFICATIONS", "VERIFIED")}
            />
            <SmallStat
              label="Rejected"
              value={stats?.verifications.rejected ?? 0}
              color={C.red}
              onClick={() => handleOpenModal("VERIFICATIONS", "REJECTED")}
            />
            <SmallStat
              label="Total"
              value={stats?.verifications.total ?? 0}
              color={C.textDark}
              onClick={() => handleOpenModal("VERIFICATIONS", "ALL")}
            />
          </div>
        </div>

        {/* Finance card */}
        <div style={cardStyle}>
          <div className="flex items-center justify-between">
            <h2 style={cardTitleStyle}>Finance</h2>
            <button
              type="button"
              onClick={() => handleOpenModal("PLATFORM_REVENUE", "ALL")}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
            >
              Financial reports
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 12,
              marginTop: 14,
            }}
          >
            <FinanceStat
              label="Platform revenue"
              value={formatRupees(stats?.finance.platformRevenuePaise ?? 0)}
              color={C.green}
              onClick={() => handleOpenModal("PLATFORM_REVENUE", "ALL")}
            />
            <FinanceStat
              label="Pending payouts to verifiers"
              value={formatRupees(stats?.finance.pendingPayoutsPaise ?? 0)}
              color={C.amber}
              onClick={() => handleOpenModal("PENDING_PAYOUTS", "ALL")}
            />
          </div>
        </div>
      </section>

      {/* Quick links / Tools */}
      <section style={cardStyle}>
        <h2 style={cardTitleStyle}>Tools &amp; Logs</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10,
            marginTop: 14,
          }}
        >
          <Link to="/admin/pending" style={toolLink}>
            Review pending applications
          </Link>
          <Link to="/admin/audit-log" style={toolLink}>
            Audit log
          </Link>
          <Link to="/admin/coupons" style={toolLink}>
            Onboarding coupons
          </Link>
          <Link to="/admin/legal" style={toolLink}>
            Terms &amp; Legal Policies
          </Link>
        </div>
      </section>

      {/* =========================================================================
          MODALS FOR STAT CARDS
          ========================================================================= */}

      {/* 1. PROFESSIONALS MODAL */}
      {activeModal === "PROFESSIONALS" && (
        <ModalShell
          title="All Registered Professionals"
          subtitle={`Viewing ${filteredProfessionals.length} of ${professionalsList.length} total individuals registered on TickAndPass.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search by name, email, phone, city..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="VERIFIED">Verified (Aadhaar/PAN)</option>
                  <option value="UNVERIFIED">Unverified</option>
                  <option value="ACTIVE">Active Users</option>
                  <option value="SUSPENDED">Suspended Users</option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {actionSuccessMsg && (
                <div
                  role="status"
                  className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-xs sm:text-sm font-semibold"
                >
                  {actionSuccessMsg}
                </div>
              )}
              {accountTarget && (
                <AccountSuspensionPanel
                  key={accountTarget.userId}
                  account={accountTarget}
                  onCancel={() => setAccountTarget(null)}
                  onDone={handleAccountChanged}
                />
              )}
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading professionals list...
                </div>
              ) : filteredProfessionals.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No professionals found matching your search.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Professional</th>
                        <th className="px-4 py-3">Contact</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3">KYC / Identity</th>
                        <th className="px-4 py-3">Credentials</th>
                        <th className="px-4 py-3">Account Status</th>
                        <th className="px-4 py-3">Joined</th>
                        <th className="px-4 py-3">
                          <span className="sr-only">Account action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedProfessionals.map((p) => (
                        <tr
                          key={p.id}
                          className="hover:bg-slate-50/70 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-[#16294C] font-bold text-xs flex items-center justify-center border border-slate-200">
                                {p.firstName?.charAt(0) || "P"}
                                {p.lastName?.charAt(0) || ""}
                              </div>
                              <div>
                                <div className="font-semibold">
                                  {p.fullName || "Unnamed"}
                                </div>
                                {p.isStudent && (
                                  <span className="inline-block px-1.5 py-0.2 text-[10px] font-semibold rounded bg-blue-50 text-blue-700 border border-blue-200">
                                    Student
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-800">{p.email}</div>
                            <div className="text-slate-500 text-xs">
                              {p.phone || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {p.city
                              ? [p.city, p.state].filter(Boolean).join(", ")
                              : "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              {p.aadhaarVerification ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 w-fit">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                  Aadhaar OKYC
                                </span>
                              ) : null}
                              {p.panVerification ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 w-fit">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                  PAN ({p.panVerification.category || "Indiv"})
                                </span>
                              ) : null}
                              {!p.aadhaarVerification && !p.panVerification && (
                                <span className="text-slate-400 text-xs">
                                  Unverified
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 font-semibold">
                            {p.credentialsCount}{" "}
                            <span className="font-normal text-xs text-slate-400">
                              creds
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
                                p.status === "ACTIVE"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-red-50 text-red-700 border border-red-200"
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                            {p.createdAt
                              ? new Date(p.createdAt).toLocaleDateString(
                                  "en-IN",
                                )
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {suspensionActionFor(p.status) && (
                              <button
                                type="button"
                                onClick={() => {
                                  setActionSuccessMsg(null);
                                  setAccountTarget(p);
                                }}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                              >
                                {suspensionActionFor(p.status) === "SUSPEND"
                                  ? "Suspend"
                                  : "Reinstate"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredProfessionals.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredProfessionals.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* 2. INSTITUTIONS MODAL */}
      {activeModal === "INSTITUTIONS" && (
        <ModalShell
          title="All Academic Institutions"
          subtitle={`Viewing ${filteredInstitutions.length} of ${institutionsList.length} total colleges & universities.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search institution name, reg no, email, city..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING_REVIEW">Pending Review</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading academic institutions...
                </div>
              ) : filteredInstitutions.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No institutions found matching your search.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Institution</th>
                        <th className="px-4 py-3">Registration / Code</th>
                        <th className="px-4 py-3">Primary Admin Contact</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3">Officers</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedInstitutions.map((inst) => (
                        <tr
                          key={inst.id}
                          onClick={() => setSelectedInstDetail(inst)}
                          className="hover:bg-slate-50/90 transition-colors cursor-pointer group"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900">
                            <div className="font-semibold text-[#16294C] group-hover:text-purple-700 transition-colors flex items-center gap-1.5">
                              <span>{inst.name}</span>
                              <span className="text-[10px] text-purple-600 opacity-0 group-hover:opacity-100 font-normal transition-opacity underline">
                                View Details
                              </span>
                            </div>
                            {inst.websiteUrl && inst.websiteUrl !== "null" && (
                              <a
                                href={
                                  inst.websiteUrl.startsWith("http")
                                    ? inst.websiteUrl
                                    : `https://${inst.websiteUrl}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {inst.websiteUrl.replace(/^https?:\/\//, "")}
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                            {inst.registrationNumber || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-800 font-medium">
                              {inst.primaryOfficer?.fullName ||
                                inst.contactEmail}
                            </div>
                            <div className="text-slate-500 text-xs">
                              {inst.contactPhone || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {inst.city ? `${inst.city}, ${inst.state}` : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-semibold">
                            {inst.officersCount}{" "}
                            <span className="font-normal text-xs text-slate-400">
                              officers
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
                                inst.status === "APPROVED"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : inst.status === "PENDING_REVIEW"
                                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                                    : "bg-red-50 text-red-700 border border-red-200"
                              }`}
                            >
                              {inst.status === "PENDING_REVIEW"
                                ? "Pending Review"
                                : inst.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                            {inst.createdAt
                              ? new Date(inst.createdAt).toLocaleDateString(
                                  "en-IN",
                                )
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInstDetail(inst);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-colors cursor-pointer"
                            >
                              <span>View</span>
                              <svg
                                className="w-3.5 h-3.5 text-slate-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredInstitutions.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredInstitutions.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* Institution Details Sub-Modal */}
      {selectedInstDetail && (
        <InstitutionDetailsModal
          inst={selectedInstDetail}
          onClose={() => setSelectedInstDetail(null)}
          onUpdated={(updatedInst) => {
            setSelectedInstDetail((prev) => ({ ...prev, ...updatedInst }));
            setInstitutionsList((prev) =>
              prev.map((i) =>
                i.id === updatedInst.id ? { ...i, ...updatedInst } : i,
              ),
            );
          }}
        />
      )}

      {/* 3. ORGANISATIONS MODAL */}
      {activeModal === "ORGANISATIONS" && (
        <ModalShell
          title="All Registered Organisations"
          subtitle={`Viewing ${filteredOrganisations.length} of ${organisationsList.length} total corporate employers & companies.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search company name, GST/CIN, email, city..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING_REVIEW">Pending Review</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading organisations list...
                </div>
              ) : filteredOrganisations.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No organisations found matching your search.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Organisation</th>
                        <th className="px-4 py-3">Registration / GSTIN</th>
                        <th className="px-4 py-3">Primary Admin Contact</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3">Branches</th>
                        <th className="px-4 py-3">Officers</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedOrganisations.map((org) => (
                        <tr
                          key={org.id}
                          onClick={() => setSelectedOrgDetail(org)}
                          className="hover:bg-slate-50/90 transition-colors cursor-pointer group"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900">
                            <div className="font-semibold text-[#16294C] group-hover:text-emerald-700 transition-colors flex items-center gap-1.5">
                              <span>{org.name}</span>
                              <span className="text-[10px] text-emerald-600 opacity-0 group-hover:opacity-100 font-normal transition-opacity underline">
                                View Details
                              </span>
                            </div>
                            {org.websiteUrl && org.websiteUrl !== "null" && (
                              <a
                                href={
                                  org.websiteUrl.startsWith("http")
                                    ? org.websiteUrl
                                    : `https://${org.websiteUrl}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {org.websiteUrl.replace(/^https?:\/\//, "")}
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                            {org.registrationNumber || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-800 font-medium">
                              {org.primaryOfficer?.fullName || org.contactEmail}
                            </div>
                            <div className="text-slate-500 text-xs">
                              {org.contactPhone || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {org.city ? `${org.city}, ${org.state}` : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-semibold">
                            {org.branchesCount || 0}{" "}
                            <span className="font-normal text-xs text-slate-400">
                              branches
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-semibold">
                            {org.officersCount || 0}{" "}
                            <span className="font-normal text-xs text-slate-400">
                              officers
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
                                org.status === "APPROVED"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : org.status === "PENDING_REVIEW"
                                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                                    : "bg-red-50 text-red-700 border border-red-200"
                              }`}
                            >
                              {org.status === "PENDING_REVIEW"
                                ? "Pending Review"
                                : org.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                            {org.createdAt
                              ? new Date(org.createdAt).toLocaleDateString(
                                  "en-IN",
                                )
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrgDetail(org);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-colors cursor-pointer"
                            >
                              <span>View</span>
                              <svg
                                className="w-3.5 h-3.5 text-slate-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredOrganisations.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredOrganisations.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* Organisation Details Sub-Modal */}
      {selectedOrgDetail && (
        <OrganisationDetailsModal
          org={selectedOrgDetail}
          onClose={() => setSelectedOrgDetail(null)}
          onUpdated={(updatedOrg) => {
            setSelectedOrgDetail((prev) => ({ ...prev, ...updatedOrg }));
            setOrganisationsList((prev) =>
              prev.map((o) =>
                o.id === updatedOrg.id ? { ...o, ...updatedOrg } : o,
              ),
            );
          }}
        />
      )}

      {/* 4. PENDING REVIEW MODAL (With Inline Approval & Rejection) */}
      {activeModal === "PENDING_REVIEW" && (
        <ModalShell
          title="Applications Awaiting Platform Review"
          subtitle={`Review and decision applications from institutions and employers (${filteredPending.length} pending).`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search pending applications..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20"
                >
                  <option value="ALL">All Types</option>
                  <option value="INSTITUTION">Institutions Only</option>
                  <option value="ORGANISATION">Organisations Only</option>
                </select>
              </div>
            </div>

            {/* Body Content */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {actionSuccessMsg && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-xs sm:text-sm font-semibold flex items-center justify-between">
                  <span>{actionSuccessMsg}</span>
                  <button
                    onClick={() => setActionSuccessMsg(null)}
                    aria-label="Close"
                    className="text-emerald-700 hover:text-emerald-900 font-bold ml-2"
                  >
                    <InlineIcon name="close" size={14} />
                  </button>
                </div>
              )}

              {modalError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold">
                  {modalError}
                </div>
              )}

              {/* Decision Input Confirmation Card (if deciding) */}
              {actionTarget && (
                <div className="p-4 bg-slate-50 border border-slate-300 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                      {actionTarget.mode === "approve"
                        ? "Approve Application"
                        : "Reject Application"}
                      :{" "}
                      <span className="text-[#2563eb]">
                        {actionTarget.item.name}
                      </span>
                    </h3>
                    <button
                      onClick={() => setActionTarget(null)}
                      className="text-xs text-slate-500 hover:text-slate-800 font-semibold"
                    >
                      Cancel
                    </button>
                  </div>

                  <p className="text-xs text-slate-600">
                    {actionTarget.mode === "approve"
                      ? "Add an optional internal approval note for the audit log:"
                      : "Please provide the reason for rejection (required, visible to applicant):"}
                  </p>

                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder={
                      actionTarget.mode === "approve"
                        ? "Optional approval note..."
                        : "Enter specific reason for rejection..."
                    }
                    rows={2}
                    className="w-full text-xs sm:text-sm p-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 bg-white"
                  />

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setActionTarget(null)}
                      disabled={actionSubmitting}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDecidePending}
                      disabled={actionSubmitting}
                      className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-all shadow-sm ${
                        actionTarget.mode === "approve"
                          ? "bg-emerald-600 hover:bg-emerald-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {actionSubmitting
                        ? "Submitting..."
                        : actionTarget.mode === "approve"
                          ? "Confirm Approval"
                          : "Confirm Rejection"}
                    </button>
                  </div>
                </div>
              )}

              {modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading pending queue...
                </div>
              ) : filteredPending.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No pending applications awaiting review!
                </div>
              ) : (
                <div className="space-y-3">
                  {paginatedPending.map((entry) => {
                    const { item, kind, reviewReason, panVerification } = entry;
                    const adminOfficer = item.officers?.[0];

                    return (
                      <div
                        key={item.id}
                        className="p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all shadow-sm space-y-3"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider ${
                                  kind === "INSTITUTION"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {kind}
                              </span>
                              <h3 className="text-sm sm:text-base font-bold text-[#16294C]">
                                {item.name}
                              </h3>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              Reg/GSTIN:{" "}
                              <span className="font-mono text-slate-700">
                                {item.registrationNumber}
                              </span>
                              {item.city && ` - ${item.city}, ${item.state}`}
                            </div>
                          </div>

                          {/* Review trigger badge */}
                          {reviewReason && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold w-fit">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                              Flag: {reviewReason}
                            </span>
                          )}
                        </div>

                        {/* Details row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                          <div>
                            <span className="text-slate-400 block">
                              Primary Officer:
                            </span>
                            <span className="font-semibold text-slate-800">
                              {adminOfficer?.fullName || "-"}
                            </span>
                            <span className="text-slate-500 block text-[11px]">
                              {adminOfficer?.user?.email || item.contactEmail}
                            </span>
                          </div>

                          <div>
                            <span className="text-slate-400 block">
                              PAN Verification:
                            </span>
                            {panVerification ? (
                              <span className="font-semibold text-slate-800">
                                {panVerification.registeredName || "Verified"}{" "}
                                (Cat: {panVerification.category || "P"})
                              </span>
                            ) : (
                              <span className="text-slate-500">
                                Standard GST
                              </span>
                            )}
                          </div>

                          <div>
                            <span className="text-slate-400 block">
                              Submitted On:
                            </span>
                            <span className="text-slate-700">
                              {item.createdAt
                                ? new Date(item.createdAt).toLocaleString(
                                    "en-IN",
                                  )
                                : "-"}
                            </span>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                          {item.registrationDocumentS3Key ? (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await createDownloadUrl(
                                    item.registrationDocumentS3Key,
                                  );
                                  window.open(
                                    res.downloadUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                  );
                                } catch {
                                  alert("Failed to open document.");
                                }
                              }}
                              className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                              View Uploaded Certificate
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs italic">
                              No document file
                            </span>
                          )}

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setActionTarget({ item, kind, mode: "reject" });
                                setActionReason("");
                              }}
                              className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActionTarget({
                                  item,
                                  kind,
                                  mode: "approve",
                                });
                                setActionReason("");
                              }}
                              className="px-4 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors shadow-sm cursor-pointer"
                            >
                              Approve
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredPending.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredPending.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* 5. VERIFICATIONS MODAL */}
      {activeModal === "VERIFICATIONS" && (
        <ModalShell
          title="All Verification Requests"
          subtitle={`Viewing ${filteredVerifications.length} of ${verificationsList.length} total verification requests submitted across the platform.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search by credential, candidate, verifier..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 cursor-pointer"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="PENDING">Pending (Awaiting Decision)</option>
                  <option value="VERIFIED">Approved / Verified</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading verification requests...
                </div>
              ) : filteredVerifications.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No verification requests found matching your filter.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Credential</th>
                        <th className="px-4 py-3">Candidate / Professional</th>
                        <th className="px-4 py-3">Assigned Verifier</th>
                        <th className="px-4 py-3">Decision & Remarks</th>
                        <th className="px-4 py-3">Fee Paid</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Requested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedVerifications.map((v) => {
                        const statusConfig = {
                          VERIFIED: {
                            bg: "bg-emerald-50",
                            text: "text-emerald-700",
                            border: "border-emerald-200",
                            label: "Verified / Approved",
                          },
                          PENDING: {
                            bg: "bg-amber-50",
                            text: "text-amber-700",
                            border: "border-amber-200",
                            label: "Pending Review",
                          },
                          REJECTED: {
                            bg: "bg-red-50",
                            text: "text-red-700",
                            border: "border-red-200",
                            label: "Rejected",
                          },
                        }[v.status] || {
                          bg: "bg-slate-50",
                          text: "text-slate-700",
                          border: "border-slate-200",
                          label: v.status,
                        };

                        return (
                          <tr
                            key={v.id}
                            className="hover:bg-slate-50/70 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-900">
                                {v.credential?.title || "Credential"}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="inline-block px-1.5 py-0.2 text-[10px] font-semibold rounded bg-slate-100 text-slate-600 border border-slate-200">
                                  {v.credential?.type || "EDUCATION"}
                                </span>
                                {v.credential?.documentUrl && (
                                  <a
                                    href={createDownloadUrl(
                                      v.credential.documentUrl,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5"
                                  >
                                    View file
                                    <InlineIcon name="external" size={11} />
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">
                                {v.professional?.fullName || "Candidate"}
                              </div>
                              <div className="text-slate-500 text-xs">
                                {v.professional?.email || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">
                                {v.verifierName}
                              </div>
                              <div className="text-slate-500 text-xs flex items-center gap-1 mt-0.5">
                                <span className="text-[10px] font-medium text-slate-600 bg-slate-100 px-1 rounded">
                                  {v.verifierType}
                                </span>
                                {v.verifierEmail && (
                                  <span>{v.verifierEmail}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 max-w-xs">
                              {v.decidedByName ? (
                                <div>
                                  <div className="text-slate-800 font-medium text-xs">
                                    By: {v.decidedByName}
                                  </div>
                                  {v.decisionReason && (
                                    <div
                                      className="text-slate-500 text-xs truncate mt-0.5 italic"
                                      title={v.decisionReason}
                                    >
                                      &ldquo;{v.decisionReason}&rdquo;
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs italic">
                                  Awaiting verifier review
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {v.payment?.amountPaise ? (
                                <span className="text-emerald-700 font-bold">
                                  {formatRupees(v.payment.amountPaise)}
                                </span>
                              ) : (
                                <span className="text-slate-400 font-normal text-xs">
                                  -
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}
                              >
                                {statusConfig.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                              {new Date(v.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredVerifications.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredVerifications.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* 6. PLATFORM REVENUE MODAL */}
      {activeModal === "PLATFORM_REVENUE" && (
        <ModalShell
          title="Platform Revenue & Transaction Breakdown"
          subtitle={`Viewing ${filteredRevenue.length} of ${revenueList.length} captured transactions. Total recorded income: ${formatRupees(
            revenueList.reduce((sum, r) => sum + (r.amountPaise || 0), 0),
          )}.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search by payer, payment ID, invoice..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 cursor-pointer"
                >
                  <option value="ALL">All Transaction Categories</option>
                  <option value="VERIFICATION_FEE">Verification Fees</option>
                  <option value="INSTITUTION_SUBSCRIPTION">
                    Institution Subscriptions
                  </option>
                  <option value="ORGANISATION_SUBSCRIPTION">
                    Organisation Subscriptions
                  </option>
                  <option value="PROFESSIONAL_MEMBERSHIP">
                    Professional Membership
                  </option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading revenue transactions...
                </div>
              ) : filteredRevenue.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No revenue transactions found matching your criteria.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Transaction / Description</th>
                        <th className="px-4 py-3">Payer / Customer</th>
                        <th className="px-4 py-3">Payment Type</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Payment Reference</th>
                        <th className="px-4 py-3">Invoice</th>
                        <th className="px-4 py-3">Captured At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedRevenue.map((r) => {
                        const typeBadgeConfig =
                          {
                            VERIFICATION_FEE:
                              "bg-blue-50 text-blue-700 border-blue-200",
                            INSTITUTION_SUBSCRIPTION:
                              "bg-purple-50 text-purple-700 border-purple-200",
                            ORGANISATION_SUBSCRIPTION:
                              "bg-indigo-50 text-indigo-700 border-indigo-200",
                            PROFESSIONAL_MEMBERSHIP:
                              "bg-teal-50 text-teal-700 border-teal-200",
                          }[r.type] ||
                          "bg-slate-50 text-slate-700 border-slate-200";

                        return (
                          <tr
                            key={r.id}
                            className="hover:bg-slate-50/70 transition-colors"
                          >
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              <div>{r.description}</div>
                              <div className="text-[11px] text-slate-400 font-normal">
                                Provider: {r.provider || "RAZORPAY"}
                              </div>
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {r.payerName}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-block px-2.5 py-0.5 text-[11px] font-semibold rounded-full border ${typeBadgeConfig}`}
                              >
                                {r.type.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-bold text-emerald-700 text-sm">
                                {formatRupees(r.amountPaise)}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-600">
                              <div>{r.providerPaymentId || r.paymentId}</div>
                              {r.providerOrderId && (
                                <div className="text-[10px] text-slate-400 font-sans">
                                  Order: {r.providerOrderId}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                              {r.invoiceNumber ? (
                                <span className="px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200 text-slate-700">
                                  {r.invoiceNumber}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                              {new Date(
                                r.paidAt || r.createdAt,
                              ).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredRevenue.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredRevenue.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}

      {/* 7. PENDING PAYOUTS MODAL */}
      {activeModal === "PENDING_PAYOUTS" && (
        <ModalShell
          title="Verifier Payouts & Ledger Payables"
          subtitle={`Viewing ${filteredPayouts.length} of ${payoutsList.length} verifier revenue-share ledger entries.`}
          onClose={handleCloseModal}
          maxWidth="max-w-6xl"
          fixedHeight={true}
        >
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
            {/* Search & Filter Bar */}
            <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search by verifier name, reg no, credential..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                />
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 cursor-pointer"
                >
                  <option value="ALL">All Payout Records</option>
                  <option value="PENDING">Pending Payouts (Unsettled)</option>
                  <option value="SETTLED">Settled Payouts</option>
                  <option value="INSTITUTION">Academic Institutions</option>
                  <option value="ORGANISATION">
                    Organisations / Employers
                  </option>
                </select>
              </div>
            </div>

            {/* Content Table Body */}
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-4">
              {modalLoading || modalError ? null : (
                <AwaitingPayouts
                  entries={payoutsList}
                  onRecorded={reloadPayouts}
                />
              )}
              {modalError && !modalLoading ? (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
                >
                  {modalError}
                </div>
              ) : modalLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Loading verifier payouts ledger...
                </div>
              ) : filteredPayouts.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No payout records found matching your filter.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Verifier / Beneficiary</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Verified Credential</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Settlement Status</th>
                        <th className="px-4 py-3">Batch / Ref</th>
                        <th className="px-4 py-3">Earned Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedPayouts.map((p) => (
                        <tr
                          key={p.id}
                          className="hover:bg-slate-50/70 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">
                              {p.verifierName}
                            </div>
                            <div className="text-slate-500 text-xs">
                              {p.contactEmail || p.regNumber || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full border ${
                                p.verifierType === "INSTITUTION"
                                  ? "bg-purple-50 text-purple-700 border-purple-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }`}
                            >
                              {p.verifierType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">
                              {p.credentialTitle}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              {p.description}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {isRefund(p) ? (
                              <span className="font-bold text-red-700 text-sm">
                                {ledgerAmountText(p)}
                              </span>
                            ) : (
                              <span className="font-bold text-amber-700 text-sm">
                                {formatRupees(p.amountPaise)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <PayoutStatusPill entry={p} />
                          </td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                            {p.settlementBatchId ||
                              (p.isSettled ? "Settled" : "Awaiting batch")}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                            {new Date(p.createdAt).toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            {!modalLoading && filteredPayouts.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredPayouts.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </ModalShell>
      )}
    </AppShell>
  );
}

/**
 * Interactive Stat Card with hover effects and clickable action indicator
 */
function StatCard({ label, value, highlight, icon, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "16px 18px",
        background: highlight ? C.amberLight : hovered ? "#F8FAFC" : C.bg,
        border: `1.5px solid ${
          hovered ? C.blue : highlight ? C.amber + "55" : C.divider
        }`,
        borderRadius: 10,
        cursor: "pointer",
        transition: "all 0.15s ease",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered
          ? "0 4px 12px rgba(0,0,0,0.06)"
          : "0 1px 3px rgba(0,0,0,0.02)",
        position: "relative",
      }}
    >
      <div className="flex items-center justify-between">
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: highlight ? C.amber : C.textMed,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </div>
        <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 hover:text-slate-600 px-1.5 py-0.5 rounded transition-colors">
          View list
        </span>
      </div>

      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: highlight ? C.amber : C.textDark,
          marginTop: 6,
          fontVariantNumeric: "tabular-nums",
          fontFamily: font,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SmallStat({ label, value, color, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isClickable = Boolean(onClick);

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => isClickable && setHovered(true)}
      onMouseLeave={() => isClickable && setHovered(false)}
      style={{
        padding: "10px 12px",
        background: hovered ? "#F1F5F9" : C.bgMuted,
        border: `1px solid ${hovered ? C.blue : "transparent"}`,
        borderRadius: 6,
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.15s ease",
        transform: hovered ? "translateY(-1px)" : "none",
        boxShadow: hovered ? "0 2px 6px rgba(0,0,0,0.05)" : "none",
      }}
      className="group relative"
    >
      <div className="flex items-center justify-between">
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </div>
        {isClickable && (
          <span className="text-[9px] font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
            <InlineIcon name="arrowRight" size={10} />
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color,
          marginTop: 2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function FinanceStat({ label, value, color, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isClickable = Boolean(onClick);

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => isClickable && setHovered(true)}
      onMouseLeave={() => isClickable && setHovered(false)}
      style={{
        padding: "12px 14px",
        background: hovered ? "#F1F5F9" : C.bgMuted,
        border: `1px solid ${hovered ? C.blue : "transparent"}`,
        borderRadius: 6,
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.15s ease",
        transform: hovered ? "translateY(-1px)" : "none",
        boxShadow: hovered ? "0 2px 6px rgba(0,0,0,0.05)" : "none",
      }}
      className="group relative"
    >
      <div className="flex items-center justify-between">
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: C.textMed,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </div>
        {isClickable && (
          <span className="text-[10px] font-semibold text-slate-400 group-hover:text-blue-600 bg-white/70 px-1.5 py-0.5 rounded transition-colors border border-slate-200">
            View breakdown
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color,
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    </div>
  );
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 20,
};

const cardTitleStyle = {
  fontSize: 14,
  fontWeight: 700,
  color: C.textDark,
  margin: 0,
};

const toolLink = {
  display: "block",
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 600,
  color: C.blue,
  textDecoration: "none",
  background: C.bgMuted,
  borderRadius: 6,
  fontFamily: font,
};

/**
 * Modern, accessible pagination bar for admin modal data tables
 */
function TablePagination({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) {
  if (totalItems === 0) return null;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(totalItems, currentPage * pageSize);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  return (
    <div className="px-4 py-3 sm:px-6 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-slate-600 shrink-0">
      <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
        <span>
          Showing{" "}
          <span className="font-semibold text-slate-800">{startItem}</span> to{" "}
          <span className="font-semibold text-slate-800">{endItem}</span> of{" "}
          <span className="font-semibold text-slate-800">{totalItems}</span>{" "}
          entries
        </span>
        <div className="flex items-center gap-1.5 border-l border-slate-300 pl-3">
          <span className="text-xs text-slate-500 font-medium">Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 text-xs rounded-md border border-slate-300 bg-white text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 cursor-pointer"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="px-2.5 py-1 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <InlineIcon name="chevronLeft" size={12} /> Previous
        </button>

        {getPageNumbers().map((p, idx) =>
          p === "..." ? (
            <span
              key={`dots-${idx}`}
              className="px-1.5 py-1 text-xs text-slate-400"
            >
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`min-w-[28px] h-7 px-2 text-xs font-semibold rounded-md border transition-all cursor-pointer ${
                currentPage === p
                  ? "bg-[#16294C] text-white border-[#16294C] shadow-sm"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="px-2.5 py-1 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          Next <InlineIcon name="chevronRight" size={12} />
        </button>
      </div>
    </div>
  );
}
